import React from 'react';

const InventoryDetailsModal = ({ open, onClose, item, onEdit }) => {
  if (!open || !item) return null;

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'approved': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      case 'quarantine': return 'bg-orange-100 text-orange-800';
      case 'expired': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStockLevelColor = (quantity, minStock) => {
    if (!minStock) return 'text-gray-600';
    if (quantity <= minStock) return 'text-red-600';
    if (quantity <= minStock * 1.5) return 'text-yellow-600';
    return 'text-green-600';
  };

  const formatCurrency = (amount) => {
    if (!amount) return 'N/A';
    
    // Get currency from supplier invoice, default to INR
    const currency = item.supplierInvoice?.currency || 'INR';
    
    // Determine the locale based on currency
    let locale = 'en-US';
    if (currency === 'INR') {
      locale = 'en-IN';
    } else if (currency === 'EUR') {
      locale = 'en-GB';
    } else if (currency === 'GBP') {
      locale = 'en-GB';
    }
    
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const isExpired = item.expiryDate && new Date(item.expiryDate) < new Date();
  const isExpiringSoon = item.expiryDate && !isExpired && 
    new Date(item.expiryDate) <= new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen p-4">
        {/* Futuristic background overlay */}
        <div className="fixed inset-0 bg-gradient-to-br from-blue-900/90 via-purple-900/90 to-indigo-900/90 backdrop-blur-sm" onClick={onClose}></div>
        
        {/* Modern modal panel */}
        <div className="relative bg-white/95 backdrop-blur-md rounded-2xl shadow-2xl w-full max-w-5xl mx-auto overflow-hidden border border-white/20">
          {/* Header with gradient */}
          <div className="bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 p-6 text-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                  <span className="text-2xl">📦</span>
                </div>
                <div>
                  <h2 className="text-2xl font-bold">{item.productSku?.name || item.productSku?.product?.name || 'Unknown Product'}</h2>
                  <p className="text-blue-100 text-sm">
                    {item.productSku?.product?.itemCode && `Code: ${item.productSku.product.itemCode}`}
                    {item.productSku?.sku && ` • SKU: ${item.productSku.sku}`}
                    {item.productSku?.barcode && ` • Barcode: ${item.productSku.barcode}`}
                    {` • Location: ${item.location || 'N/A'}`}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => onEdit(item)}
                  className="px-4 py-2 bg-white/20 rounded-lg hover:bg-white/30 transition-colors flex items-center space-x-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
                  </svg>
                  <span>Edit</span>
                </button>
                <button
                  onClick={onClose}
                  className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center hover:bg-white/30 transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                  </svg>
                </button>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            {/* Stock Status Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4 border border-blue-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-blue-600">Current Stock</p>
                    <p className={`text-2xl font-bold ${getStockLevelColor(item.quantity, item.minStock)}`}>
                      {item.quantity || 0}
                    </p>
                  </div>
                  <div className="w-10 h-10 bg-blue-200 rounded-lg flex items-center justify-center">
                    <span className="text-blue-600 text-lg">📊</span>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-4 border border-green-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-green-600">Min Stock</p>
                    <p className="text-2xl font-bold text-green-700">
                      {item.minStock || 'N/A'}
                    </p>
                  </div>
                  <div className="w-10 h-10 bg-green-200 rounded-lg flex items-center justify-center">
                    <span className="text-green-600 text-lg">⚠️</span>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-4 border border-purple-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-purple-600">Unit Cost</p>
                    <p className="text-2xl font-bold text-purple-700">
                      {formatCurrency(item.unitCost)}
                    </p>
                  </div>
                  <div className="w-10 h-10 bg-purple-200 rounded-lg flex items-center justify-center">
                    <span className="text-purple-600 text-lg">💰</span>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-4 border border-orange-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-orange-600">Total Value</p>
                    <p className="text-2xl font-bold text-orange-700">
                      {formatCurrency((item.quantity || 0) * (item.unitCost || 0))}
                    </p>
                  </div>
                  <div className="w-10 h-10 bg-orange-200 rounded-lg flex items-center justify-center">
                    <span className="text-orange-600 text-lg">💎</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Main Content Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Left Column - Basic Information */}
              <div className="space-y-6">
                <div className="bg-white rounded-xl border border-gray-200 p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <span className="mr-2">📋</span>
                    Basic Information
                  </h3>
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-gray-500">Product Name</label>
                        <p className="text-gray-900 font-medium">{item.productSku?.name || item.productSku?.product?.name || 'N/A'}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Product Code</label>
                        <p className="text-gray-900 font-medium">{item.productSku?.product?.itemCode || 'N/A'}</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-gray-500">SKU</label>
                        <p className="text-gray-900 font-medium">{item.productSku?.sku || 'N/A'}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Barcode</label>
                        <p className="text-gray-900 font-medium">{item.productSku?.barcode || 'N/A'}</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-gray-500">Location</label>
                        <p className="text-gray-900 font-medium">{item.location || 'N/A'}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Batch Number</label>
                        <p className="text-gray-900 font-medium">{item.batch || 'N/A'}</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-gray-500">Currency</label>
                        <p className="text-gray-900 font-medium">{item.supplierInvoice?.currency || 'INR'}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Supplier</label>
                        <p className="text-gray-900 font-medium">{item.supplier?.name || 'N/A'}</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-gray-500">Expiry Date</label>
                        <div className="flex items-center space-x-2">
                          <p className={`font-medium ${isExpired ? 'text-red-600' : isExpiringSoon ? 'text-yellow-600' : 'text-gray-900'}`}>
                            {formatDate(item.expiryDate)}
                          </p>
                          {isExpired && <span className="text-red-500 text-sm">⚠️ Expired</span>}
                          {isExpiringSoon && <span className="text-yellow-500 text-sm">⚠️ Expiring Soon</span>}
                        </div>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Quality Status</label>
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(item.qualityStatus)}`}>
                          {item.qualityStatus || 'Not Set'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Supplier Information */}
                <div className="bg-white rounded-xl border border-gray-200 p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <span className="mr-2">🏢</span>
                    Supplier Information
                  </h3>
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-gray-500">Supplier Name</label>
                        <p className="text-gray-900 font-medium">{item.supplierName || 'N/A'}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Supplier Code</label>
                        <p className="text-gray-900 font-medium">{item.supplierCode || 'N/A'}</p>
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Contact Information</label>
                      <p className="text-gray-900 font-medium">{item.supplierContact || 'N/A'}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Column - Additional Details */}
              <div className="space-y-6">
                {/* Item Description */}
                <div className="bg-white rounded-xl border border-gray-200 p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <span className="mr-2">📝</span>
                    Item Description
                  </h3>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-gray-700 whitespace-pre-wrap">
                      {item.itemDescription || 'No description available'}
                    </p>
                  </div>
                </div>

                {/* Storage & Quality */}
                <div className="bg-white rounded-xl border border-gray-200 p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <span className="mr-2">🔧</span>
                    Storage & Quality
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium text-gray-500">Storage Conditions</label>
                      <p className="text-gray-900 font-medium">{item.storageConditions || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Quality Status</label>
                      <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getStatusColor(item.qualityStatus)}`}>
                        {item.qualityStatus || 'Not Set'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Notes */}
                <div className="bg-white rounded-xl border border-gray-200 p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <span className="mr-2">📌</span>
                    Notes
                  </h3>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-gray-700 whitespace-pre-wrap">
                      {item.notes || 'No notes available'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer with additional actions */}
            <div className="mt-8 pt-6 border-t border-gray-200">
              <div className="flex justify-between items-center">
                <div className="text-sm text-gray-500">
                  Last updated: {formatDate(item.updatedAt)}
                </div>
                <div className="flex space-x-3">
                  <button
                    onClick={() => onEdit(item)}
                    className="px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all"
                  >
                    Edit Item
                  </button>
                  <button
                    onClick={onClose}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InventoryDetailsModal; 